// ---------------------------------------------------------------------------
// BOT-MODELL - eine Skala fuer alle Modi (15.08.2026)
//
// WARUM ES DIESE DATEI GIBT
// Bots gab es an zwei Stellen, mit zwei getrennten Vorstellungen davon, was
// "stark" heisst:
//
//   501     ein Ziel-Average von 20 bis 120, Trefferquoten daraus gerechnet
//   Secure  drei fest verdrahtete Stufen (1, 2, 3) mit rohen Zahlen mitten
//           im Spielcode - "if (rnd > 0.6) hit = 1" in zwei Funktionen
//
// Eine neue Stufe hiess damit: zwei Dateien anfassen, sechs Zahlenreihen
// von Hand ausbalancieren, und die Stufen der beiden Modi hatten trotzdem
// nichts miteinander zu tun - "Stufe 2" in Secure war irgendwo, aber nicht
// auf der Average-Skala von 501.
//
// JETZT: EINE ZAHL BESCHREIBT EINEN BOT - sein 3-Dart-Average. Alles andere
// wird daraus gerechnet. Eine neue Stufe ist eine Zeile in BOT_STUFEN.
//
// WARUM DER AVERAGE UND NICHT EINE ABSTRAKTE "STAERKE" VON 0 BIS 1
// Weil der Average die Zahl ist, die ein Dartspieler ohnehin kennt und
// nachrechnen kann. Ein "Stufe 3"-Bot ist eine Behauptung; ein
// "Average 85"-Bot ist eine Ansage, an der man ihn messen kann.
//
// EICHUNG (bewusst dokumentiert, damit spaetere Aenderungen wissen, wogegen
// sie sich vergleichen)
//
//   Die Formeln fuer Doppel, Triple und Setup-Singles sind WOERTLICH die
//   des bisherigen 501-Bots. Der Umbau darf 501 nicht staerker oder
//   schwaecher machen - er verschiebt nur, wo die Formeln stehen.
//
//   Neu ist nur der Wurf auf eine bestimmte ZAHL (Secure, Bob's & Co.).
//   Er ist an den drei alten Secure-Stufen geeicht:
//
//     alte Stufe 1  ~ Average  35   Fehlwurf 40 %, Triple  2 %
//     alte Stufe 2  ~ Average  65   Fehlwurf 10 %, Triple 15 %
//     alte Stufe 3  ~ Average 120   Fehlwurf  1 %, Triple 60 %
//
// ZUFALL WIRD HEREINGEREICHT, NICHT GEZOGEN
// Jede Wurffunktion nimmt `rnd` (0 bis 100) als letzten Parameter. Ohne
// Angabe wuerfelt sie selbst. Das kostet nichts und macht das ganze Modell
// pruefbar: Ein Test kann jede Grenze genau treffen, statt zehntausend Mal
// zu wuerfeln und zu hoffen.
// ---------------------------------------------------------------------------

export const BOT_AVG_MIN = 20;
export const BOT_AVG_MAX = 120;

/** Die benannten Stufen. EINE Zeile je Stufe - das ist der ganze Punkt.
 *
 *  `id` geht in die Uebersetzung (`bot.stufe.<id>`), `avg` ist das Modell,
 *  `akzent` nur die Farbe des Knopfes. Reihenfolge = Anzeigereihenfolge. */
export const BOT_STUFEN = [
  { id: "neuling", avg: 20, akzent: "accent-green" },
  { id: "anfaenger", avg: 30, akzent: "accent-green" },
  { id: "hobby", avg: 38, akzent: "accent-cyan" },
  { id: "freizeit", avg: 45, akzent: "accent-cyan" },
  { id: "verein", avg: 53, akzent: "accent-blue" },
  { id: "liga", avg: 60, akzent: "accent-blue" },
  { id: "ambitioniert", avg: 70, akzent: "accent-purple" },
  { id: "profi", avg: 85, akzent: "accent-purple" },
  { id: "halbprofi", avg: 95, akzent: "accent-orange" },
  { id: "weltklasse", avg: 105, akzent: "accent-orange" },
  { id: "nuke", avg: 120, akzent: "accent-red" },
];

export function begrenzeAvg(avg) {
  const zahl = Number(avg);
  if (!Number.isFinite(zahl)) return 40;
  return Math.min(BOT_AVG_MAX, Math.max(BOT_AVG_MIN, Math.round(zahl)));
}

/** Stufe zu einer id - oder null. */
export function botStufe(id) {
  return BOT_STUFEN.find((s) => s.id === id) || null;
}

/** Die Stufe, die einem Average am naechsten kommt.
 *
 *  Gebraucht fuer die Beschriftung des 501-Reglers: Wer dort 87 einstellt,
 *  soll trotzdem lesen koennen, wo er ungefaehr steht ("Profi"). */
export function stufeFuerAvg(avg) {
  const wert = begrenzeAvg(avg);
  let beste = BOT_STUFEN[0];
  for (const stufe of BOT_STUFEN) {
    if (Math.abs(stufe.avg - wert) < Math.abs(beste.avg - wert)) beste = stufe;
  }
  return beste;
}

// ── Trefferquoten (in Prozent) ─────────────────────────────────────────────
//
// Alle vier stammen unveraendert aus dem 501-Bot. Sie stehen hier, damit es
// EINE Stelle gibt, an der die Staerke eines Bots definiert ist.

/** Doppel treffen, wenn ausdruecklich darauf gezielt wird (Checkout). */
export function chanceDoppel(avg) {
  return Math.max(5, (begrenzeAvg(avg) - 20) * 0.7);
}

/** Triple treffen, wenn auf das Triple gezielt wird (Scoring). */
export function chanceTriple(avg) {
  return Math.max(0, (begrenzeAvg(avg) - 30) * 0.65);
}

/** Statt des Triples wenigstens das Single derselben Zahl. */
export function chanceSingleStattTriple(avg, tripleChance) {
  return Math.min(100 - tripleChance, 20 + begrenzeAvg(avg) * 0.7);
}

/** Ein bestimmtes Single-Feld treffen (Stellwurf). */
export function chanceSingle(avg) {
  return Math.min(99, 40 + begrenzeAvg(avg) * 0.5);
}

// ── Wurf auf eine bestimmte ZAHL (Secure & Co.) ────────────────────────────

/** Anteil der Treffer, die im Triple landen, wenn auf die ZAHL gezielt wird.
 *
 *  Nicht auf das Triple gezielt - auf die Zahl. Ein starker Spieler steht
 *  trotzdem mit dem Wurfbild im oberen Segmentteil und nimmt das Triple
 *  haeufig mit; ein Anfaenger trifft die Zahl irgendwo.
 *
 *  Geradlinig ueber dem Average, gedeckelt bei 60 % - genau die drei
 *  Eichpunkte oben (Average 35 → 6 %, 65 → 25 %, 120 → 59 %). */
export function anteilTriple(avg) {
  return Math.min(0.6, Math.max(0, (begrenzeAvg(avg) - 25) / 160));
}

/** Anteil der Treffer im Doppelring derselben Zahl.
 *
 *  Fast unabhaengig vom Koennen: Der Doppelring ist gleich breit, egal wer
 *  wirft, und niemand zielt beim Sichern darauf. Die alten Stufen lagen bei
 *  13 %, 17 % und 20 % - ein fester Wert trifft das besser als eine Formel,
 *  die Genauigkeit vortaeuscht. */
export const ANTEIL_DOPPEL = 0.16;

/** Die Bull-Scheibe (25 oder 50) mit EINEM Dart treffen, in Prozent.
 *
 *  KORREKTUR 17.08.2026. Die alte Kurve war `15 + avg * 1,05`. Sie stammte
 *  aus den drei handgesetzten Secure-Stufen und war schlicht zu gut:
 *
 *      Average  alt je Dart   alt je Aufnahme   neu je Dart   neu je Aufnahme
 *         30       46,5 %          84,6 %          6,6 %          18,5 %
 *         45       62,3 %          94,6 %         14,9 %          38,4 %
 *         60       78,0 %          98,9 %         23,1 %          54,6 %
 *         85       95,0 %          99,9 %         36,9 %          74,9 %
 *        105       95,0 %          99,9 %         47,9 %          85,8 %
 *
 *  Ein Freizeitspieler sicherte damit 19 von 20 Aufnahmen - er traf das
 *  Bull haeufiger, als es ein Weltklassespieler in Wirklichkeit tut.
 *
 *  Die neue Gerade ist an dem geeicht, was Spieler beim Bull-Training
 *  erreichen: rund jeder siebte Dart bei einem 45er-Average, gut jeder
 *  dritte beim Profi, knapp jeder zweite an der Weltspitze. Der Deckel bei
 *  58 % haelt auch THE NUKE unterhalb des Unmoeglichen.
 *
 *  Auswirkung im Spiel: Sichern ist wieder eine Leistung, und die letzte
 *  Stufe der Leiter (BULLSEYE) ist eine echte Huerde - bewusst so, siehe
 *  UMSETZUNG_2026-08-17. */
export function chanceBull(avg) {
  return Math.min(58, Math.max(2, (begrenzeAvg(avg) - 18) * 0.55));
}

/** Anteil der Bulltreffer, die im INNEREN Bull (Bullseye, 50) landen.
 *
 *  Die Untergrenze ist keine Schaetzung, sondern Geometrie: Das Bullseye
 *  hat 6,35 mm Radius, die ganze Bull-Scheibe 15,9 mm - das innere Feld
 *  nimmt (6,35/15,9)^2 = 16 % der Flaeche ein. Wer die Scheibe nur
 *  zufaellig trifft, landet also in 16 % der Faelle im Bullseye; darunter
 *  kann niemand liegen. Nach oben waechst der Anteil mit dem Koennen, weil
 *  ein guter Spieler auf die MITTE zielt und nicht auf die Scheibe.
 *
 *  Alt war eine Spanne von 5 % bis 55 % - die 5 % lagen unter dem
 *  geometrischen Boden, die 55 % darueber, was Zielen hergibt. */
export function anteilBullseye(avg) {
  return Math.min(0.45, Math.max(0.16, 0.16 + (begrenzeAvg(avg) - 40) / 260));
}

// ── Darts ──────────────────────────────────────────────────────────────────

/** Ein Dart des Bots als vollstaendiges Feld, nicht als blosse Zahl.
 *
 *  Dieselbe Form wie ein Hand- oder Trackerdart (board-input.js,
 *  aufnahme-eingabe.js), damit die Anzeige nicht zwei Sorten kennen muss.
 *  Stand bis 15.08.2026 in game-501.js und ist von dort unveraendert
 *  hierhergezogen - der Secure-Bot braucht dieselben Darts. */
export function botDart(base, mult) {
  const b = Number(base) || 0;
  const m = b === 0 ? 1 : Number(mult) || 1;
  const label =
    b === 0
      ? "MISS"
      : b === 50
        ? "B-EYE"
        : b === 25
          ? "BULL"
          : (m === 3 ? "T" : m === 2 ? "D" : "") + b;
  return {
    base: b,
    multiplier: m,
    mult: m,
    points: b * m,
    label,
    ring:
      b === 50
        ? "bullseye"
        : b === 25
          ? "bull"
          : m === 3
            ? "triple"
            : m === 2
              ? "double"
              : b === 0
                ? "miss"
                : "single",
    quelle: "bot",
  };
}

const wuerfel = () => Math.random() * 100;

/** Wurf auf ein Doppel (Checkout). `ziel` ist der Rest: 40 → D20, 50 → Bull. */
export function wurfAufDoppel(avg, ziel, rnd = wuerfel()) {
  const chance = chanceDoppel(avg);
  if (rnd < chance) {
    return ziel === 50 ? botDart(50, 1) : botDart(ziel / 2, 2);
  }
  if (rnd < chance + 15) {
    // Single statt Doppel (innen) - beim Bull das aeussere Bull.
    return ziel === 50 ? botDart(25, 1) : botDart(ziel / 2, 1);
  }
  return botDart(0, 1); // knapp vorbei
}

/** Wurf auf ein Triple (Scoring). `single` ist die Zahl, also 20 fuer T20. */
export function wurfAufTriple(avg, single, rnd = wuerfel()) {
  const chanceT = chanceTriple(avg);
  const chanceS = chanceSingleStattTriple(avg, chanceT);
  if (rnd < chanceT) return botDart(single, 3);
  if (rnd < chanceT + chanceS) return botDart(single, 1);
  return botDart(1, 1); // abgerutscht in die 1
}

/** Stellwurf auf ein Single-Feld. */
export function wurfAufSingle(avg, feld, rnd = wuerfel()) {
  if (rnd < chanceSingle(avg)) return botDart(feld, 1);
  return botDart(Math.floor(Math.random() * 20) + 1, 1); // kompletter Fail
}

/** Wurf auf eine bestimmte ZAHL - Secure, Bob's, Round the World.
 *
 *  Gefragt ist hier nicht "wie viele Punkte", sondern "diese Zahl, ja oder
 *  nein - und in welchem Ring". Der Rueckgabewert ist deshalb immer
 *  entweder die Zielzahl in einem der drei Ringe oder ein Fehlwurf.
 *
 *  `zahl` ist 1..20 oder 25/50 (Bull). Bei 50 gibt es kein Doppel: Das
 *  Bullseye IST der innere Ring, ein "Doppel-Bullseye" existiert nicht. */
export function wurfAufZahl(avg, zahl, rnd = wuerfel()) {
  const ziel = Number(zahl);

  if (ziel === 25 || ziel === 50) {
    const chance = chanceBull(avg);
    if (rnd >= chance) return botDart(0, 1);
    // Innerhalb der Treffer: aussen oder innen?
    const innen = (rnd / chance) * 100 < anteilBullseye(avg) * 100;
    if (ziel === 50) return innen ? botDart(50, 1) : botDart(0, 1);
    return innen ? botDart(50, 1) : botDart(25, 1);
  }

  const chance = chanceSingle(avg);
  if (rnd >= chance) return botDart(0, 1);
  // Innerhalb der Treffer den Ring bestimmen. Der Anteil wird auf die
  // Trefferspanne umgerechnet, damit EIN rnd fuer beide Entscheidungen
  // reicht - sonst braeuchte ein Test zwei Wuerfe je Dart.
  const anteil = rnd / chance;
  const pT = anteilTriple(avg);
  if (anteil < pT) return botDart(ziel, 3);
  if (anteil < pT + ANTEIL_DOPPEL) return botDart(ziel, 2);
  return botDart(ziel, 1);
}

// ---------------------------------------------------------------
// Bruecke zu window: game-501.js und game-secure.js sind keine Module im
// Sinne von Importen (sie haengen ueber window zusammen), und die
// Konsole soll die Stufen zum Nachsehen erreichen.
// ---------------------------------------------------------------
if (typeof window !== "undefined") {
  Object.assign(window, {
    BOT_STUFEN,
    BOT_AVG_MIN,
    BOT_AVG_MAX,
    botStufe,
    stufeFuerAvg,
    begrenzeAvg,
    botDart,
    wurfAufDoppel,
    wurfAufTriple,
    wurfAufSingle,
    wurfAufZahl,
    botModell: {
      chanceDoppel,
      chanceTriple,
      chanceSingleStattTriple,
      chanceSingle,
      chanceBull,
      anteilTriple,
      anteilBullseye,
      ANTEIL_DOPPEL,
    },
  });
}
